import { useCallback } from "react";

import { ExternalGrantStatusEnum } from "@/__generated__/graphql";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { DEFAULT_OPEN_THREAD_TABS } from "@/state/threadTabs";

/**
 * The books of the businesses the person is in shared with firms (``given``) -
 * a business shares with every firm it works with, each by a grant of its own
 * - and the books others share with them (``received``), each asked for or
 * standing; of those, the ones only asked that are theirs to take up
 * (``asked``): asked of their address, or of a firm they own - the firm's other
 * people see them once it has.
 */
export function useExternalGrants() {
  const { data, loading, refetch } = useBackendQuery(
    Queries.EXTERNAL_GRANTS_QUERY,
    { fetchPolicy: "cache-and-network" },
  );
  const { accounts } = useScope();
  const grants = data?.externalGrants?.data ?? [];
  const received = grants.filter((grant) => !grant.isGiven);
  const owned = new Set(
    (accounts ?? [])
      .filter((account) => account.isOwner)
      .map((account) => account.entity?.identifier),
  );
  return {
    given: grants.filter((grant) => grant.isGiven),
    received,
    asked: received.filter(
      (grant) =>
        grant.grantStatus === ExternalGrantStatusEnum.Invited &&
        (grant.granteeIdentifier == null || owned.has(grant.granteeIdentifier)),
    ),
    isLoading: loading && !data,
    refetch,
  };
}

/** The accounting firms Neoledger verified: those a client picks by name. */
export function useVerifiedFirms() {
  const { data, loading } = useBackendQuery(Queries.VERIFIED_FIRMS_QUERY);
  return {
    firms: data?.verifiedFirms?.data ?? [],
    isLoading: loading && !data,
  };
}

/**
 * Open a client's books - the grants their businesses are shared through,
 * opened together - or, given none, go back to the person's own. The app
 * starts again in those books: nothing read in the others - a cached list, a
 * selected business, an open chat - is carried into them, and the socket
 * connects again naming them.
 */
export function useOpenClientBooks() {
  return useCallback((externalGrantIdentifiers: string[]) => {
    State.setState({
      externalGrantIdentifiers,
      activeEntityIdentifiers: [],
      scopedEntityIdentifier: null,
      entities: [],
      activeThreadNodeId: null,
      openThreadTabs: DEFAULT_OPEN_THREAD_TABS,
      userIdentifier: null,
    });
    window.location.assign(
      externalGrantIdentifiers.length > 0 ? "/app/triage" : "/app/clients",
    );
  }, []);
}

/**
 * Share a business's books with a firm - asked of someone there by their
 * address, or of a verified firm itself - allowed no more than one of the
 * business's own roles.
 */
export function useInviteExternalGrantee() {
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.INVITE_EXTERNAL_GRANTEE,
  });
  const invite = useCallback(
    (
      entityIdentifier: string,
      asked: { emailAddress: string } | { firmIdentifier: string },
      roleIdentifier: string,
      onSuccess?: () => void,
    ) =>
      mutate({
        variables: { input: { entityIdentifier, ...asked, roleIdentifier } },
        refetchQueries: ["ExternalGrants"],
        callbacks: { onSuccess },
      }),
    [mutate],
  );
  return { invite, loading };
}

/** Give a firm a business shares its books with another of the business's roles. */
export function useChangeExternalGrantRole() {
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.CHANGE_EXTERNAL_GRANT_ROLE,
  });
  const changeRole = useCallback(
    (externalGrantIdentifier: string, roleIdentifier: string) =>
      mutate({
        variables: { input: { externalGrantIdentifier, roleIdentifier } },
        refetchQueries: ["ExternalGrants"],
      }),
    [mutate],
  );
  return { changeRole, loading };
}

/** Stop sharing a business's books with a firm - or, as the firm, give them back. */
export function useRevokeExternalGrant() {
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.REVOKE_EXTERNAL_GRANT,
  });
  const revoke = useCallback(
    (externalGrantIdentifier: string) =>
      mutate({
        variables: { input: { externalGrantIdentifier } },
        refetchQueries: ["ExternalGrants", "ExternalClients"],
      }),
    [mutate],
  );
  return { revoke, loading };
}
